import {
	type LoaderFunctionArgs,
	json,
	type ActionFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import {
	NavLink,
	useFetcher,
	useLoaderData,
	useLocation,
} from '@remix-run/react'
import { useEffect } from 'react'
import { Brand } from '#app/components/brand.js'
import { GeneralErrorBoundary } from '#app/components/error-boundary.js'
import Customizer from '#app/components/tiptap/customizer/customizer.js'
import { useCustomizer } from '#app/components/tiptap/hooks/useCustomizer.js'
import {
	DEFAULT_FAVICON,
	DEFAULT_OGIMAGE,
	PAGE_TYPES,
} from '#app/utils/constants.js'
import { getComputedData } from '#app/utils/customizer.server.js'
import { prisma } from '#app/utils/db.server.js'
import { generatePageStyles, injectScripts } from '#app/utils/styling.js'
import { signup } from '#app/utils/sub.model.js'

export const FORM_KEY = 'sign-up-form'

export async function loader({ params }: LoaderFunctionArgs) {
	const url = params.url

	const list = await prisma.list.findFirstOrThrow({
		where: { OR: [{ url }, { id: url }] },
		include: {
			pages: { where: { type: PAGE_TYPES.SIGNUP }, include: { theme: true } },
			settings: true,
			domain: true,
			theme: true,
			metrics: true,
			user: {
				select: {
					isPro: true,
				},
			},
		},
	})

	const { listData } = await getComputedData({ list })

	const page = list.pages.find(p => p.type === PAGE_TYPES.SIGNUP)

	return json({
		list: { ...list, ...listData },
		isPro: list.user.isPro,
		page,
	})
}

export async function action({ params, request }: ActionFunctionArgs) {
	const { res } = await (await signup(params, request)).json()

	if (res.redirect) return json({ success: true })
	else return json(res)
}

export default function ProjectPage() {
	const { list, isPro, page } = useLoaderData<typeof loader>()
	const fetcher = useFetcher({ key: FORM_KEY })
	const content = page?.content
	const theme = page?.theme

	const { editor } = useCustomizer({
		content,
		// @ts-ignore
		data: { list },
		mode: 'page',
		editable: false,
	})

	useEffect(() => {
		// @ts-ignore
		injectScripts({ page: page ?? {}, list })
	}, [page, list])

	return (
		<div style={{ backgroundColor: theme?.backgroundColor ?? '' }}>
			{theme && (
				<style
					dangerouslySetInnerHTML={{ __html: generatePageStyles(theme) }}
				/>
			)}

			<fetcher.Form
				method="post"
				className="mx-auto h-full min-h-screen w-full max-w-[516px] content-center px-[24px] pb-[128px] pt-[128px]"
			>
				{editor && <Customizer editor={editor} />}
			</fetcher.Form>
			{!isPro && (
				<>
					<div className="gradient-blur">
						<div></div>
						<div></div>
						<div></div>
						<div></div>
						<div></div>
						<div></div>
					</div>
					<div className="fixed !left-1/2 bottom-0 !-translate-x-1/2 transform p-6">
						<div className="flex w-80 items-center justify-center">
							<p className="font-medium">Powered by</p>
							<NavLink
								className="flex scale-90"
								to="/admin/lists"
								prefetch="intent"
							>
								<Brand variant="logo" />
							</NavLink>
						</div>
					</div>
				</>
			)}
		</div>
	)
}

export function ErrorBoundary() {
	const location = useLocation()
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: () => (
					<div className="flex flex-col gap-6">
						<div className="flex flex-col gap-3">
							<h1 className="whitespace-pre-wrap break-all text-body-lg">
								We can't find this page:
							</h1>
							<pre className="break-all text-body-lg">{location.pathname}</pre>
						</div>
					</div>
				),
			}}
		/>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	const page = data?.page
	const list = data?.list

	const title = page?.title || list?.title || `${data?.list.name}`
	const desc =
		page?.desc ||
		list?.socialDescription ||
		`Sign up to ${data?.list.name} to get early access, product updates, and more.`
	const ogImage = page?.ogImage || list?.ogImage || DEFAULT_OGIMAGE
	const favicon = page?.favicon || list?.favicon || DEFAULT_FAVICON

	return [
		{ title },
		{ tagName: 'link', rel: 'icon', href: favicon },
		{ property: 'og:image', content: ogImage },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: desc },
		{ tagName: 'link', rel: 'apple-touch-icon', href: favicon },
		{
			tagName: 'link',
			rel: 'icon',
			type: 'image/png',
			sizes: '16x16',
			href: favicon,
		},
		{
			tagName: 'link',
			rel: 'icon',
			type: 'image/png',
			sizes: '32x32',
			href: favicon,
		},
	]
}
